<script lang="ts">
  import type { Segment } from '$lib/stores/aiFeatures'
  import { diffRenderer } from '$lib/services/DiffRenderer'
  import { settingsStore, settingsActions } from '$lib/stores/settings'
  import { AIService } from '$lib/services/AIService'
  import { AI_MODELS } from '$lib/config/aiModels.config'
  import { slide } from 'svelte/transition'
  import { 
    Check, 
    X, 
    Undo2, 
    Redo2, 
    AlertCircle, 
    RefreshCw, 
    ChevronDown, 
    MessageSquare, 
    PenLine, 
    Maximize2, 
    Minimize2, 
    Send,
    GitCompare
  } from 'lucide-svelte'
  
  // Provider icons
  import openaiIcon from '../assets/ai-model-icons/openai.png'
  import claudeIcon from '../assets/ai-model-icons/claude.png'
  import googleIcon from '../assets/ai-model-icons/google.png'
  import openrouterIcon from '../assets/ai-model-icons/openrouter.png'
  
  interface Props {
    segment: Segment
    index: number
    mode: 'paraphrase' | 'expand' | 'shrink' | 'prompt'
    onApply: () => void
    onIgnore: () => void
    onContinue?: () => void
    onExpand?: () => void
    onShrink?: () => void
    onParaphrase?: () => void
    onPrompt?: () => void
    onGeneratePrompt?: (prompt: string, modelId: string) => void
    onHover?: (segmentId: string | null) => void
    onUndo?: () => void
    onRedo?: () => void
    canUndo?: boolean
    canRedo?: boolean
    showIndex?: boolean
    customPrompt?: string
    hideDiffButton?: boolean
    hideReason?: boolean
  }
  
  let { 
    segment, 
    index, 
    mode,
    onApply,
    onIgnore,
    onContinue,
    onExpand,
    onShrink,
    onParaphrase,
    onPrompt,
    onGeneratePrompt,
    onHover,
    onUndo,
    onRedo,
    canUndo = false,
    canRedo = false,
    showIndex = true,
    customPrompt,
    hideDiffButton = false,
    hideReason = false
  }: Props = $props()
  
  let isHovered = $state(false)
  let isCardProcessing = $derived(segment.isProcessing || false)
  let showDiff = $state(false) // State to toggle diff display (default: false for compact view)
  let showPromptInput = $state(false) // Show prompt input in card
  let promptInput = $state('')
  let savedPromptInput = '' // Save prompt input when closing without submitting
  let showModelDropdown = $state(false)
  let modelSelectorRef = $state<HTMLDivElement | null>(null)
  let promptTextareaRef = $state<HTMLTextAreaElement | null>(null)
  let generationBarRef = $state<HTMLDivElement | null>(null)
  let isPromptProcessing = $state(false) // Processing state for prompt submission
  let ignoreNextClick = $state(false) // Flag to ignore click after model selection
  let dropdownDirection = $state<'up' | 'down'>('down') // Dropdown direction
  let showReason = $state(false) // State to toggle reason display (default: collapsed)
  
  
  
  // Get diff stats - recalculated when segment changes (including after undo/redo)
  let diffStats = $derived(
    segment.suggested && segment.suggested !== segment.original
      ? diffRenderer.getDiffStats(segment.original, segment.suggested)
      : null
  )
  
  // Get diff HTML for display
  let diffHtml = $derived(
    showDiff && segment.suggested && segment.hasChanges
      ? diffRenderer.renderInlineDiff(segment.original, segment.suggested)
      : ''
  )
  
  // Parse manual edit diff from reason
  let manualEditDiff = $derived(() => {
    if (segment.mode === 'manual-edit' && segment.reason?.startsWith('manual-edit-diff:')) {
      try {
        const jsonStr = segment.reason.substring('manual-edit-diff:'.length)
        return JSON.parse(jsonStr) as { added: string[], removed: string[] }
      } catch {
        return null
      }
    }
    return null
  })
  
  // Format diff text with highlighting
  function formatDiffText(text: string, type: 'added' | 'removed'): string {
    // Find the actual change part (between context)
    const match = text.match(/^(\.\.\..*?\s)?(.*?)(\s.*\.\.\.)?$/)
    if (!match) return text
    
    const before = match[1] || ''
    const change = match[2] || ''
    const after = match[3] || ''
    
    // Escape HTML and format
    const escape = (str: string) => str.replace(/</g, '&lt;').replace(/>/g, '&gt;')
    
    let result = ''
    if (before) result += `<span class="context">${escape(before)}</span>`
    result += `<span class="change-text ${type}">${escape(change)}</span>`
    if (after) result += `<span class="context">${escape(after)}</span>`
    
    return result
  }
  
  // Check if this is a "keep original" suggestion
  let isKeepOriginal = $derived(!segment.hasChanges && segment.suggested === segment.original)
  
  // Get settings reactively
  const settings = $derived($settingsStore)
  
  // Get selected AI model from settings store - reactive
  const currentModelId = $derived(settings.ai?.selectedModel || 'openai/gpt-oss-120b')
  
  // Get all available AI models from configuration
  let allAIModels = $derived.by(() => {
    const models = []
    const providers = settings.aiProviders || []
    
    // Get enabled providers
    const enabledProviders = providers.filter(p => p.enabled)
    
    // Add models from each enabled provider
    for (const provider of enabledProviders) {
      const providerModels = AI_MODELS[provider.id] || []
      models.push(...providerModels)
    }
    
    // If OpenRouter is not enabled, still include it (free models)
    if (!enabledProviders.some(p => p.id === 'openrouter')) {
      const openrouterModels = AI_MODELS['openrouter'] || []
      models.push(...openrouterModels)
    }
    
    return models
  })
  
  let currentAIModel = $derived(
    allAIModels.find(m => m.id === currentModelId) || 
    allAIModels.find(m => m.id === 'openai/gpt-oss-120b') || // Default to OpenRouter GPT-OSS
    null
  )
  
  // Get provider icon
  function getProviderIcon(provider: string): string {
    const iconMap: Record<string, string> = {
      'openai': openaiIcon,
      'anthropic': claudeIcon,
      'google': googleIcon,
      'openrouter': openrouterIcon
    }
    return iconMap[provider] || openrouterIcon
  }
  
  // Sort AI models
  function sortedAIModels() {
    return [...allAIModels].sort((a, b) => {
      if (a.recommended && !b.recommended) return -1
      if (!a.recommended && b.recommended) return 1
      return a.name.localeCompare(b.name)
    })
  }
  
  // Select AI model (sync with session storage for consistency)
  function selectAIModel(model: any) {
    // Update global model selection in settings
    settingsActions.updateAISettings({ selectedModel: model.id })
    showModelDropdown = false
    
    // Set flag to ignore next click to prevent closing the prompt
    ignoreNextClick = true
    setTimeout(() => {
      ignoreNextClick = false
    }, 200) // Reset flag after 200ms
  }
  
  // Check if dropdown should open upward
  function checkDropdownDirection() {
    if (!modelSelectorRef) return
    
    const rect = modelSelectorRef.getBoundingClientRect()
    const dropdownHeight = 300 // Approximate max height of dropdown
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    
    // Open upward if not enough space below and more space above
    dropdownDirection = spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'up' : 'down'
  }
  
  // Toggle model dropdown
  function toggleModelDropdown(e: Event) {
    e.stopPropagation()
    showModelDropdown = !showModelDropdown
    if (showModelDropdown) {
      checkDropdownDirection()
    }
  }
  
  // Close dropdown on outside click
  $effect(() => {
    if (showModelDropdown) {
      const handleClickOutside = (e: MouseEvent) => {
        if (modelSelectorRef && !modelSelectorRef.contains(e.target as Node)) {
          showModelDropdown = false
        }
      }
      
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  })
  
  // Auto-focus textarea when prompt input is shown
  $effect(() => {
    if (showPromptInput && promptTextareaRef) {
      // Restore saved input when opening
      if (savedPromptInput) {
        promptInput = savedPromptInput
      }
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        promptTextareaRef?.focus()
      }, 10)
    }
  })
  
  // Handle click outside to close prompt input
  $effect(() => {
    if (!showPromptInput) return
    
    function handleClickOutside(e: MouseEvent) {
      // Ignore click if flag is set (right after model selection)
      if (ignoreNextClick) {
        return
      }
      
      const target = e.target as HTMLElement
      
      // Check if clicked on the model selector or dropdown
      if (modelSelectorRef && modelSelectorRef.contains(target)) {
        // Don't close if clicking on model selector
        return
      }
      
      // Check if clicked outside the generation bar
      if (generationBarRef && !generationBarRef.contains(target)) {
        // Also check if it's not the prompt button itself
        const promptButton = target.closest('.tool-btn')
        if (!promptButton) {
          // Save the current input before closing
          if (promptInput.trim()) {
            savedPromptInput = promptInput
          }
          showPromptInput = false
        }
      }
    }
    
    // Add slight delay to prevent immediate close on open
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 10)
    
    return () => {
      clearTimeout(timer)
      document.removeEventListener('click', handleClickOutside)
    }
  })
  
  // Handle prompt submission
  async function handlePromptSubmit() {
    if (!promptInput.trim() || !onGeneratePrompt) return
    
    // Use the current synchronized model ID
    const modelId = currentModelId
    if (!modelId) return
    
    const savedPrompt = promptInput.trim()
    
    // Clear input and close prompt input immediately
    promptInput = ''
    savedPromptInput = '' // Clear saved input after submission
    showPromptInput = false
    
    try {
      // Call the generation handler
      // The handler should set segment.isProcessing = true
      await onGeneratePrompt(savedPrompt, modelId)
    } catch (error) {
      console.error('Failed to generate:', error)
      // Error will be handled by parent component through segment.error
    }
  }
  
  // Auto-resize textarea based on content
  function autoResizeTextarea(e: Event) {
    const textarea = e.target as HTMLTextAreaElement
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }
  
  // Handle prompt input with max length
  function handlePromptInput(e: Event) {
    const textarea = e.target as HTMLTextAreaElement
    if (textarea.value.length > 200) {
      promptInput = textarea.value.substring(0, 200)
    }
    autoResizeTextarea(e)
  }
  
  // Handle keyboard events with proper copy/paste support
  function handleKeyDown(e: KeyboardEvent) {
    // Allow standard text editing shortcuts
    if (e.metaKey || e.ctrlKey) {
      // Don't prevent default for copy, paste, cut, select all, undo, redo
      if (['c', 'v', 'x', 'a', 'z'].includes(e.key.toLowerCase())) {
        // Allow default behavior for these shortcuts
        return
      }
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handlePromptSubmit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      showPromptInput = false
      promptInput = ''
    }
  }
  
  // Handle prompt button click
  function handlePromptClick() {
    if (showPromptInput) {
      // If already showing, close it
      showPromptInput = false
      promptInput = ''
    } else {
      // Show prompt input
      showPromptInput = true
      // Focus will be set via $effect
      // Don't call onPrompt if we have onGeneratePrompt (to avoid showing editor bottom prompt)
      // Only call onPrompt if we don't have onGeneratePrompt (fallback behavior)
      if (!onGeneratePrompt && onPrompt) {
        onPrompt()
      }
    }
  }
  
  // Get mode label - use segment's mode if available, otherwise use prop mode
  let modeLabel = $derived(
    // If AI suggests keeping original, show "원문유지"
    isKeepOriginal ? '원문유지' :
    (segment.mode || mode) === 'manual-edit' ? '사용자 수정' :
    (segment.mode || mode) === 'continue' ? '이어쓰기' :
    (segment.mode || mode) === 'expand' ? '늘려쓰기' :
    (segment.mode || mode) === 'shrink' ? '줄이기' :
    (segment.mode || mode) === 'paraphrase' ? '바꿔쓰기' :
    (segment.mode || mode) === 'prompt' ? '프롬프트' :
    '개선됨'
  )
  
  // Get mode class for styling
  let modeClass = $derived(
    isKeepOriginal ? 'keep-original' : (segment.mode || mode)
  )
  
  // Check if card is processed
  let isApplied = $derived(segment.status === 'applied')
  let isIgnored = $derived(segment.status === 'skipped')
  let isProcessed = $derived(isApplied || isIgnored)
  
  function handleMouseEnter() {
    onHover?.(segment.id)

    // Use viewId if available, otherwise fallback to segment.id
    const viewId = segment.viewId || segment.id

    // Dispatch only one event for inline view highlighting
    // This prevents multiple handlers from processing the same hover
    window.dispatchEvent(new CustomEvent('inline-view-hover', {
      detail: { viewId }
    }))

    // Legacy event for backward compatibility if needed
    window.dispatchEvent(new CustomEvent('ai-card-hover', { detail: { segmentId: segment.id } }))
  }

  function handleMouseLeave() {
    onHover?.(null)

    // Dispatch only one event for inline view hover end
    window.dispatchEvent(new CustomEvent('inline-view-hover-end'))

    // Legacy event for backward compatibility if needed
    window.dispatchEvent(new CustomEvent('ai-card-hover-end'))
  }
  
  async function handleAction(action: () => void | Promise<void>) {
    await action()
  }
  
  // Listen for hover events from SplitEditor and inline views
  let isHoveredFromPanel = $state(false)
  let isHoveredFromInline = $state(false)
  let isHoveredFromDecoration = $state(false)

  $effect(() => {
    const handleSegmentHover = (e: CustomEvent) => {
      if (e.detail?.segmentId === segment.id) {
        isHoveredFromPanel = true
        // Auto-scroll to this card
        const cardElement = document.querySelector(
          `[data-segment-id="${segment.id}"]`
        ) as HTMLElement

        if (cardElement) {
          const container = cardElement.closest('.ai-cards-list, .errors-list, .inline-view-cards-list') as HTMLElement
          if (container) {
            const containerRect = container.getBoundingClientRect()
            const cardRect = cardElement.getBoundingClientRect()
            const relativeTop = cardElement.offsetTop

            // Smooth scroll to center the card in view
            const targetScroll = relativeTop - (containerRect.height / 2) + (cardRect.height / 2)

            container.scrollTo({
              top: Math.max(0, targetScroll),
              behavior: 'smooth'
            })
          }
        }
      }
    }

    const handleSegmentHoverEnd = () => {
      isHoveredFromPanel = false
    }

    // Handle inline view hover events
    const handleInlineHover = (e: CustomEvent) => {
      // Check both viewId and segment.id for compatibility
      const matchesView = segment.viewId && e.detail?.viewId === segment.viewId
      const matchesSegment = e.detail?.viewId === segment.id


      if (matchesView || matchesSegment) {
        isHoveredFromInline = true
        // Auto-scroll to this card
        const cardElement = document.querySelector(
          `[data-segment-id="${segment.id}"]`
        ) as HTMLElement

        if (cardElement) {
          const container = cardElement.closest('.inline-view-cards-list, .errors-list') as HTMLElement
          if (container) {
            const containerRect = container.getBoundingClientRect()
            const cardRect = cardElement.getBoundingClientRect()
            const relativeTop = cardElement.offsetTop

            // Only scroll if card is not fully visible
            const currentScrollTop = container.scrollTop
            const containerHeight = containerRect.height
            const cardTop = relativeTop
            const cardBottom = cardTop + cardRect.height

            if (cardTop < currentScrollTop || cardBottom > currentScrollTop + containerHeight) {
              const targetScroll = relativeTop - (containerHeight / 2) + (cardRect.height / 2)
              container.scrollTo({
                top: Math.max(0, targetScroll),
                behavior: 'smooth'
              })
            }
          }
        }
      }
    }

    const handleInlineHoverEnd = () => {
      isHoveredFromInline = false
    }

    // Handle decoration hover events (from editor text highlights)
    const handleDecorationHover = (e: CustomEvent) => {
      const matchesView = segment.viewId && e.detail?.viewId === segment.viewId
      const matchesSegment = e.detail?.viewId === segment.id

      if (matchesView || matchesSegment) {
        isHoveredFromDecoration = true
        // Auto-scroll to this card
        const cardElement = document.querySelector(
          `[data-segment-id="${segment.id}"]`
        ) as HTMLElement

        if (cardElement) {
          // Find the actual scrollable container
          // Try multiple selectors to find the scrollable parent
          let container = cardElement.closest('.error-display-panel') as HTMLElement

          if (!container) {
            // Try to find the error display content area
            container = cardElement.closest('.error-display-content') as HTMLElement
          }

          if (!container) {
            // Try to find any scrollable parent
            let parent = cardElement.parentElement
            while (parent) {
              const overflow = window.getComputedStyle(parent).overflowY
              if (overflow === 'auto' || overflow === 'scroll') {
                container = parent as HTMLElement
                break
              }
              parent = parent.parentElement
            }
          }

          if (container) {
            try {
              const containerRect = container.getBoundingClientRect()
              const cardRect = cardElement.getBoundingClientRect()

              // Calculate relative position more accurately
              const relativeTop = cardRect.top - containerRect.top
              const cardHeight = cardRect.height
              const containerHeight = containerRect.height
              const currentScrollTop = container.scrollTop

              // Calculate actual position in scrollable content
              const cardTopInContainer = relativeTop + currentScrollTop

              // Check if card is not fully visible
              const isAboveView = cardTopInContainer < currentScrollTop + 20 // 20px margin
              const isBelowView = (cardTopInContainer + cardHeight) > (currentScrollTop + containerHeight - 20)

              if (isAboveView || isBelowView) {
                // Center the card in the viewport
                const targetScroll = Math.max(0, cardTopInContainer - (containerHeight / 2) + (cardHeight / 2))

                container.scrollTo({
                  top: targetScroll,
                  behavior: 'smooth'
                })
              }

              // Add highlight effect
              cardElement.classList.add('decoration-hover')
            } catch (error) {
              console.error('Error scrolling to card on decoration hover:', error)
              // Still add highlight effect even if scroll fails
              cardElement.classList.add('decoration-hover')
            }
          }
        }
      }
    }

    const handleDecorationHoverEnd = () => {
      isHoveredFromDecoration = false
      // Remove highlight effect from card
      const cardElement = document.querySelector(
        `[data-segment-id="${segment.id}"]`
      ) as HTMLElement
      if (cardElement) {
        cardElement.classList.remove('decoration-hover')
      }
    }

    window.addEventListener('segment-hover', handleSegmentHover as EventListener)
    window.addEventListener('segment-hover-end', handleSegmentHoverEnd)
    window.addEventListener('inline-card-hover', handleInlineHover as EventListener)
    window.addEventListener('inline-card-leave', handleInlineHoverEnd)
    window.addEventListener('inline-decoration-hover', handleDecorationHover as EventListener)
    window.addEventListener('inline-decoration-hover-end', handleDecorationHoverEnd)

    return () => {
      window.removeEventListener('segment-hover', handleSegmentHover as EventListener)
      window.removeEventListener('segment-hover-end', handleSegmentHoverEnd)
      window.removeEventListener('inline-card-hover', handleInlineHover as EventListener)
      window.removeEventListener('inline-card-leave', handleInlineHoverEnd)
      window.removeEventListener('inline-decoration-hover', handleDecorationHover as EventListener)
      window.removeEventListener('inline-decoration-hover-end', handleDecorationHoverEnd)
    }
  })
</script>

<div
  class="ai-action-card"
  class:has-changes={segment.hasChanges}
  class:selected={segment.selected}
  class:applied={isApplied}
  class:ignored={isIgnored}
  class:hovered-from-panel={isHoveredFromPanel}
  class:hovered-from-inline={isHoveredFromInline}
  class:hovered-from-decoration={isHoveredFromDecoration}
  class:split-view={showIndex}
  data-segment-id={segment.id}
  role="article"
  aria-label={`Segment ${index + 1} suggestion`}
  onmouseenter={() => {
    isHovered = true
    handleMouseEnter()
  }}
  onmouseleave={() => {
    isHovered = false
    handleMouseLeave()
  }}
>
  <div class="card-header">
    <div class="segment-info">
      {#if showIndex}
        <span class="segment-number">{index + 1}</span>
      {/if}
      <span class="mode-badge {modeClass}">{modeLabel}</span>
      {#if diffStats}
        <span class="diff-stats">
          <span class="stat-add">+{diffStats.additions}</span>
          <span class="stat-del">-{diffStats.deletions}</span>
        </span>
      {/if}
    </div>
    <div class="card-actions">
      <!-- Diff toggle button -->
      {#if !hideDiffButton}
        <button
          class="action-icon diff-toggle"
          class:active={showDiff}
          onclick={() => showDiff = !showDiff}
          title={showDiff ? '비교 숨기기' : '비교 보기'}
          aria-label={showDiff ? '비교 숨기기' : '비교 보기'}
          disabled={!segment.hasChanges}
        >
          <GitCompare size={12} />
        </button>
      {/if}
      
      <!-- Apply button (check icon) -->
      <button
        class="action-icon apply"
        class:active={isApplied}
        onclick={() => handleAction(onApply)}
        disabled={isCardProcessing || !segment.hasChanges}
        title={isApplied ? '적용됨' : '적용'}
        aria-label={isApplied ? '적용됨' : '적용'}
      >
        <Check size={12} />
      </button>
      <!-- Ignore button (X icon) -->
      <button
        class="action-icon ignore"
        class:active={isIgnored}
        onclick={() => handleAction(onIgnore)}
        disabled={isCardProcessing}
        title={isIgnored ? '무시됨' : '무시'}
        aria-label={isIgnored ? '무시됨' : '무시'}
      >
        <X size={12} />
      </button>
      
      <!-- Separator if history actions are available -->
      {#if onUndo || onRedo}
        <div class="action-separator"></div>
      {/if}
      
      <!-- Undo button -->
      {#if onUndo}
        <button
          class="action-icon undo"
          onclick={() => onUndo?.()}
          disabled={!canUndo || isCardProcessing}
          title="실행 취소"
          aria-label="실행 취소"
        >
          <Undo2 size={12} />
        </button>
      {/if}
      
      <!-- Redo button -->
      {#if onRedo}
        <button
          class="action-icon redo"
          onclick={() => onRedo?.()}
          disabled={!canRedo || isCardProcessing}
          title="다시 실행"
          aria-label="다시 실행"
        >
          <Redo2 size={12} />
        </button>
      {/if}
    </div>
  </div>
  
  <div class="card-content">
    {#if segment.error}
      <!-- Error state -->
      <div class="error-state">
        <div class="error-message">
          <AlertCircle class="error-icon" size={14} />
          <span class="error-text">AI 처리 실패: {segment.error}</span>
        </div>
        {#if segment.retryCallback}
          <button 
            class="retry-btn"
            onclick={() => segment.retryCallback?.()}
            disabled={isCardProcessing}
          >
            <RefreshCw size={12} />
            재시도
          </button>
        {/if}
      </div>
    {:else if isCardProcessing && !segment.isProcessing}
      <div class="loading-indicator">
        <div class="spinner"></div>
        <span class="loading-text">AI가 처리 중...</span>
      </div>
    {:else}
      <!-- Show diff or simple text based on toggle -->
      {#if segment.original && segment.original.length <= 100 && !showIndex}
        <!-- Short text in inline view (not in split view) -->
        {#if showDiff && diffHtml}
          <!-- Show inline diff when diff toggle is on -->
          <div class="diff-preview">
            {@html diffHtml}
          </div>
        {:else}
          <!-- Show original and suggested text when diff is off -->
          <div class="text-preview">
            {#if (segment.mode || mode) === 'prompt' && (segment.customPrompt || customPrompt)}
              <div class="prompt-text">
                <span class="text-label">프롬프트:</span>
                <span class="text-content">{segment.customPrompt || customPrompt}</span>
              </div>
            {/if}
            <div class="original-text">
              <span class="text-label">원본:</span>
              <span class="text-content preserve-format">{segment.original}</span>
            </div>
            {#if segment.suggested}
              <div class="suggested-text">
                <span class="text-label">제안:</span>
                <span class="text-content preserve-format">{segment.suggested}</span>
                {#if segment.isProcessing}
                  <span class="streaming-indicator">...</span>
                {/if}
              </div>
            {:else if segment.isProcessing}
              <div class="loading-text">AI가 생성 중...</div>
            {:else if !segment.suggested}
              <div class="loading-text">AI가 분석 중...</div>
            {/if}
          </div>
        {/if}
      {:else if segment.original && segment.original.length <= 100 && showIndex}
        <!-- Short text in split view - use same display as long text split view -->
        <div class="splitview-content">
          {#if segment.reason && !segment.reason.startsWith('manual-edit-diff:') && segment.hasChanges && segment.mode !== 'prompt' && mode !== 'prompt'}
            <div class="reason-display">
              {segment.reason}
            </div>
          {:else if (segment.mode || mode) === 'prompt' && (segment.customPrompt || customPrompt)}
            <div class="prompt-display">
              <span class="prompt-label">프롬프트:</span>
              <span class="prompt-value">{segment.customPrompt || customPrompt}</span>
            </div>
          {:else if segment.isProcessing}
            <div class="loading-text">
              AI가 생성 중
              <span class="streaming-indicator">...</span>
            </div>
          {:else if !segment.suggested}
            <div class="loading-text">AI가 분석 중...</div>
          {:else if !segment.hasChanges}
            <div class="no-change-text">변경 사항 없음</div>
          {:else}
            <div class="processing-text">처리 중...</div>
          {/if}
        </div>
      {:else}
        <!-- For long segments -->
        {#if !showIndex}
          <!-- Inline view cards: show text preview -->
          {#if showDiff && diffHtml}
            <!-- Show inline diff when diff toggle is on -->
            <div class="diff-preview">
              {@html diffHtml}
            </div>
          {:else}
            <!-- Show text preview for long text -->
            <div class="text-preview">
              {#if (segment.mode || mode) === 'prompt' && (segment.customPrompt || customPrompt)}
                <div class="prompt-text">
                  <span class="text-label">프롬프트:</span>
                  <span class="text-content">{segment.customPrompt || customPrompt}</span>
                </div>
              {/if}
              <div class="original-text">
                <span class="text-label">원본:</span>
                <span class="text-content preserve-format">{segment.original}</span>
              </div>
              {#if segment.suggested}
                <div class="suggested-text">
                  <span class="text-label">제안:</span>
                  <span class="text-content preserve-format">{segment.suggested}</span>
                  {#if segment.isProcessing}
                    <span class="streaming-indicator">...</span>
                  {/if}
                </div>
              {:else if segment.isProcessing}
                <div class="loading-text">AI가 생성 중...</div>
              {:else if !segment.suggested}
                <div class="loading-text">AI가 분석 중...</div>
              {:else}
                <div class="no-change-text">변경 사항 없음</div>
              {/if}
            </div>
          {/if}
        {:else}
          <!-- Split view: show only reason or status -->
          <div class="splitview-content">
            {#if segment.reason && !segment.reason.startsWith('manual-edit-diff:') && segment.hasChanges && segment.mode !== 'prompt' && mode !== 'prompt'}
              <div class="reason-display">
                {segment.reason}
              </div>
            {:else if (segment.mode || mode) === 'prompt' && (segment.customPrompt || customPrompt)}
              <div class="prompt-display">
                <span class="prompt-label">프롬프트:</span>
                <span class="prompt-value">{segment.customPrompt || customPrompt}</span>
              </div>
            {:else if segment.isProcessing}
              <div class="loading-text">
                AI가 생성 중
                <span class="streaming-indicator">...</span>
              </div>
            {:else if !segment.suggested}
              <div class="loading-text">AI가 분석 중...</div>
            {:else if !segment.hasChanges}
              <div class="no-change-text">변경 사항 없음</div>
            {:else}
              <div class="processing-text">처리 중...</div>
            {/if}
          </div>
        {/if}
      {/if}
      
      <!-- Manual edit diff display -->
      {#if manualEditDiff()}
        <div class="manual-edit-diff">
          {#if manualEditDiff().removed.length > 0}
            <div class="diff-removed">
              {#each manualEditDiff().removed as removed}
                <div class="diff-line">
                  <span class="diff-marker">-</span>
                  <span class="diff-text">{@html formatDiffText(removed, 'removed')}</span>
                </div>
              {/each}
            </div>
          {/if}
          {#if manualEditDiff().added.length > 0}
            <div class="diff-added">
              {#each manualEditDiff().added as added}
                <div class="diff-line">
                  <span class="diff-marker">+</span>
                  <span class="diff-text">{@html formatDiffText(added, 'added')}</span>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/if}
      
      <!-- Collapsible reason section (only for non-split view and not in prompt mode) -->
      {#if !hideReason && !showIndex && segment.reason && !segment.reason.startsWith('manual-edit-diff:') && segment.hasChanges && segment.mode !== 'prompt' && mode !== 'prompt'}
        <div class="reason-section">
          <button 
            class="reason-toggle"
            onclick={() => showReason = !showReason}
            aria-expanded={showReason}
            aria-label={showReason ? '변경 이유 숨기기' : '변경 이유 보기'}
          >
            <ChevronDown 
              class="toggle-icon {showReason ? 'rotated' : ''}" 
              size={10} 
            />
            <span class="reason-label">변경 이유</span>
          </button>
          {#if showReason}
            <div class="reason-content" transition:slide={{ duration: 200 }}>
              <div class="reason-text">{segment.reason}</div>
            </div>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
  
  <!-- AI tool actions (shown on hover) -->
  {#if isHovered}
    <div class="ai-tools">
      {#if onPrompt || onGeneratePrompt}
        <button 
          class="tool-btn"
          onclick={() => {
            // Always use internal prompt if onGeneratePrompt is available
            if (onGeneratePrompt) {
              handlePromptClick()
            } else if (onPrompt) {
              // Only use external prompt if onGeneratePrompt is not available
              handleAction(onPrompt)
            }
          }}
          disabled={isCardProcessing}
          title="프롬프트로 편집"
          aria-label="프롬프트"
        >
          <MessageSquare size={14} />
        </button>
      {/if}
      
      {#if onContinue}
        <button 
          class="tool-btn"
          onclick={() => {
            handleAction(onContinue)
          }}
          disabled={isCardProcessing}
          title="이어쓰기"
          aria-label="이어쓰기"
        >
          <PenLine size={14} />
        </button>
      {/if}
      
      {#if onParaphrase}
        <button 
          class="tool-btn"
          onclick={() => {
            handleAction(onParaphrase)
          }}
          disabled={isCardProcessing}
          title="다시쓰기"
          aria-label="다시쓰기"
        >
          <RefreshCw size={14} />
        </button>
      {/if}
      
      {#if onExpand}
        <button 
          class="tool-btn"
          onclick={() => {
                    handleAction(onExpand)
          }}
          disabled={isCardProcessing}
          title="늘리기"
          aria-label="늘리기"
        >
          <Maximize2 size={14} />
        </button>
      {/if}
      
      {#if onShrink}
        <button 
          class="tool-btn"
          onclick={() => {
            handleAction(onShrink)
          }}
          disabled={isCardProcessing}
          title="줄이기"
          aria-label="줄이기"
        >
          <Minimize2 size={14} />
        </button>
      {/if}
    </div>
  {/if}
</div>

<!-- Prompt Input Section (shown when prompt button is clicked) - Outside card -->
{#if showPromptInput && onGeneratePrompt}
  <div class="card-generation-bar" bind:this={generationBarRef}>
    <div class="card-chat-container">
      {#if isPromptProcessing}
        <!-- Loading or retry state -->
        <div class="card-processing-state">
          <div class="card-spinner"></div>
          <span class="card-processing-text">AI가 처리 중...</span>
          {#if segment.error && segment.retryCallback}
            <button 
              class="card-retry-icon"
              onclick={() => segment.retryCallback?.()}
              title="재시도"
              aria-label="재시도"
            >
              <RefreshCw size={14} />
            </button>
          {/if}
        </div>
      {:else}
        <!-- Main textarea -->
        <textarea
          bind:this={promptTextareaRef}
          bind:value={promptInput}
          class="card-prompt-input"
          placeholder={segment.suggested ? "이 텍스트를 어떻게 변경할까요?" : "무엇을 생성할까요?"}
          maxlength="200"
          oninput={handlePromptInput}
          onkeydown={handleKeyDown}
          disabled={isPromptProcessing}
          rows="1"
        ></textarea>
      {/if}
      
      <!-- Bottom controls -->
      <div class="card-bottom-controls">
        <!-- Model selector at bottom left -->
        <div class="card-model-selector" bind:this={modelSelectorRef}>
          <button
            class="card-model-btn"
            onclick={(e) => toggleModelDropdown(e)}
            title="AI 모델 선택"
          >
            {#if currentAIModel}
              <span class="card-model-name">{currentAIModel.name}</span>
            {:else}
              <span class="card-model-name">AI 모델</span>
            {/if}
            <ChevronDown class="card-chevron-icon {showModelDropdown ? 'rotated' : ''}" size={5} />
          </button>
          
          <!-- Model dropdown -->
          {#if showModelDropdown}
            <div class="card-model-dropdown" class:dropdown-up={dropdownDirection === 'up'}>
              <div class="card-model-list">
                {#each sortedAIModels() as model}
                  <button
                    class="card-model-item"
                    class:selected={currentModelId === model.id}
                    onclick={() => selectAIModel(model)}
                  >
                    <div class="card-item-info">
                      <div class="card-item-name">
                        {model.name}
                        {#if model.recommended}
                          <span class="card-recommended-badge">추천</span>
                        {/if}
                      </div>
                    </div>
                    {#if currentModelId === model.id}
                      <Check class="card-check-icon" size={10} />
                    {/if}
                  </button>
                {/each}
              </div>
            </div>
          {/if}
        </div>
        
        <!-- Right side controls -->
        <div class="card-right-controls">
          <!-- Character count -->
          {#if promptInput.length >= 50}
            <span class="card-char-count">{promptInput.length}/200</span>
          {/if}
          
          <!-- Send button -->
          <button
            class="card-send-btn"
            onclick={handlePromptSubmit}
            disabled={!promptInput.trim()}
            title="생성 (Enter)"
            aria-label="생성"
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Ultra-minimal AI action card - similar to error card */
  .ai-action-card {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 4px 6px;
    margin: 0 -4px 8px -4px;
    font-size: 14.5px;
    position: relative;
    transition: all 0.15s ease;
    border-left: 2px solid transparent;
    padding-left: 4px;
  }
  
  .ai-action-card:hover {
    background: rgba(0, 0, 0, 0.02);
    border-left-color: rgba(88, 86, 214, 0.3);
  }
  
  .ai-action-card.selected {
    border-left-color: var(--accent-primary);
    background: rgba(88, 86, 214, 0.05);
  }
  
  .ai-action-card.hovered-from-panel,
  .ai-action-card.hovered-from-inline,
  .ai-action-card.hovered-from-decoration {
    background: rgba(88, 86, 214, 0.12);
    border-left-color: var(--accent-primary);
    box-shadow: 0 0 0 1px rgba(88, 86, 214, 0.2);
  }

  /* Enhanced hover state for dark mode */
  :global(.dark-theme) .ai-action-card.hovered-from-panel,
  :global(.dark-theme) .ai-action-card.hovered-from-inline,
  :global(.dark-theme) .ai-action-card.hovered-from-decoration {
    background: rgba(88, 86, 214, 0.2);
    border-left-color: var(--accent-primary);
    box-shadow: 0 0 0 1px rgba(88, 86, 214, 0.4);
  }
  
  .ai-action-card.ignored {
    opacity: 0.6;
  }
  
  .ai-action-card.applied {
    opacity: 0.6;
  }
  
  .action-icon.active {
    background: rgba(34, 197, 94, 0.1);
    color: var(--success);
  }
  
  .action-icon.ignore.active {
    background: rgba(239, 68, 68, 0.1);
    color: var(--text-tertiary);
  }
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }
  
  /* Split view mode compact styles */
  .ai-action-card.split-view {
    padding: 6px 8px;
    margin-bottom: 8px;
    border-left-width: 3px;
  }
  
  .ai-action-card.split-view .card-header {
    margin-bottom: 4px;
  }
  
  .ai-action-card.split-view .splitview-content {
    padding: 4px 0;
  }
  
  .ai-action-card.split-view .reason-display {
    padding: 2px 0 2px 6px;
    font-size: 11px;
    line-height: 1.4;
    margin-top: 2px;
  }
  
  .ai-action-card.split-view:hover {
    background: rgba(88, 86, 214, 0.04);
    border-left-color: rgba(88, 86, 214, 0.5);
  }
  
  .segment-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }
  
  .segment-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    border-radius: 50%;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
  }
  
  .mode-badge {
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .mode-badge.expand {
    background: rgba(34, 197, 94, 0.1);
    color: var(--success);
  }
  
  .mode-badge.shrink {
    background: rgba(239, 68, 68, 0.1);
    color: var(--error);
  }
  
  .mode-badge.paraphrase {
    background: rgba(88, 86, 214, 0.1);
    color: var(--accent-primary);
  }
  
  .mode-badge.prompt {
    background: rgba(107, 114, 128, 0.1);
    color: var(--text-secondary);
  }
  
  /* Special style for "원문유지" mode */
  .mode-badge.keep-original {
    background: rgba(148, 163, 184, 0.1);
    color: var(--text-secondary);
  }
  
  /* Manual edit mode */
  .mode-badge.manual-edit {
    background: rgba(251, 191, 36, 0.1);
    color: #f59e0b;
  }
  
  .diff-stats {
    display: flex;
    gap: 6px;
    font-size: 11px;
    font-weight: 500;
  }
  
  .stat-add {
    color: var(--success);
  }
  
  .stat-del {
    color: var(--error);
  }
  
  /* Card action icons */
  .card-actions {
    display: flex;
    gap: 4px;
  }
  
  .action-icon {
    width: 20px;
    height: 20px;
    padding: 0;
    background: transparent;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }
  
  .action-icon:hover:not(:disabled) {
    color: var(--text-primary);
  }
  
  .action-icon.apply:hover:not(:disabled) {
    background: rgba(34, 197, 94, 0.1);
    color: var(--success);
  }
  
  .action-icon.ignore:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.1);
    color: var(--error);
  }
  
  .action-icon:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .action-icon :global(svg) {
    width: 13px;
    height: 13px;
  }
  
  .action-separator {
    width: 1px;
    height: 12px;
    background: var(--border-primary);
    margin: 0 2px;
    opacity: 0.3;
  }
  
  .action-icon.undo:hover:not(:disabled) {
    background: rgba(88, 86, 214, 0.1);
    color: var(--accent-primary);
  }
  
  .action-icon.redo:hover:not(:disabled) {
    background: rgba(88, 86, 214, 0.1);
    color: var(--accent-primary);
  }
  
  .action-icon.diff-toggle:hover:not(:disabled) {
    background: rgba(107, 114, 128, 0.1);
    color: var(--text-primary);
  }
  
  .action-icon.diff-toggle.active {
    background: rgba(107, 114, 128, 0.15);
    color: var(--accent-primary);
  }
  
  .card-content {
    margin-bottom: 6px;
    line-height: 1.45;
  }
  
  /* Split view content styles */
  .splitview-content {
    padding: 8px 0;
  }
  
  .reason-display {
    font-size: 12px;
    line-height: 1.5;
    color: var(--text-secondary);
    padding: 4px 0 4px 8px;
    background: transparent;
    border-radius: 0;
    border-left: 1px solid var(--accent-primary);
    opacity: 0.7;
  }
  
  .prompt-display {
    font-size: 12px;
    padding: 6px 0;
  }
  
  .prompt-display .prompt-label {
    font-weight: 600;
    color: var(--text-tertiary);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-right: 6px;
  }
  
  .prompt-display .prompt-value {
    color: var(--text-secondary);
    font-style: italic;
  }
  
  .splitview-content .loading-text,
  .splitview-content .no-change-text,
  .splitview-content .processing-text {
    font-size: 12px;
    color: var(--text-tertiary);
    font-style: italic;
    padding: 8px 0;
  }
  
  .reason-text {
    color: var(--text-secondary);
  }
  
  /* Collapsible reason section styles */
  .reason-section {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--border-primary);
  }
  
  .reason-toggle {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 5px;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: var(--text-tertiary);
    font-size: 11px;
    font-weight: 500;
    transition: all 0.15s ease;
    margin: -3px -5px;
  }
  
  .reason-toggle:hover {
    background: var(--bg-tertiary);
    color: var(--text-secondary);
  }
  
  .toggle-icon {
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }
  
  .toggle-icon.rotated {
    transform: rotate(180deg);
  }
  
  .reason-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .reason-content {
    margin-top: 6px;
    padding: 6px 8px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    animation: slideDown 0.2s ease;
  }
  
  .reason-section .reason-text {
    font-size: 12px;
    line-height: 1.5;
    color: var(--text-secondary);
    margin: 0;
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .loading-text {
    color: var(--text-tertiary);
    font-style: italic;
    font-size: 12px;
  }
  
  .no-change-text {
    color: var(--text-tertiary);
    font-size: 12px;
  }
  
  .streaming-indicator {
    display: inline-block;
    animation: blink 1s infinite;
    color: var(--primary-color);
    font-weight: bold;
    margin-left: 4px;
  }
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
  
  /* Text preview for short segments */
  .text-preview {
    padding: 4px 0;
  }
  
  .text-preview.compact {
    padding: 2px 0;
  }
  
  
  /* Diff preview styles */
  .diff-preview {
    padding: 4px 0;
    font-size: 13px;
    line-height: 1.5;
  }
  
  .diff-preview :global(.inline-diff-view) {
    display: block;
  }
  
  .diff-preview :global(.diff-unchanged) {
    color: var(--text-secondary);
  }
  
  .diff-preview :global(.diff-removed) {
    background: rgba(239, 68, 68, 0.15);
    color: var(--error, #ef4444);
    text-decoration: line-through;
    padding: 0 2px;
    border-radius: 2px;
  }
  
  .diff-preview :global(.diff-added) {
    background: rgba(34, 197, 94, 0.15);
    color: var(--success, #22c55e);
    padding: 0 2px;
    border-radius: 2px;
  }
  
  /* Dark mode diff styles */
  :global(:root.dark-theme) .diff-preview :global(.diff-removed) {
    background: rgba(239, 68, 68, 0.25);
  }
  
  :global(:root.dark-theme) .diff-preview :global(.diff-added) {
    background: rgba(34, 197, 94, 0.25);
  }
  
  .original-text,
  .suggested-text,
  .prompt-text {
    font-size: 14px;
    line-height: 1.5;
    margin: 2px 0;  /* Reduced from 4px */
  }
  
  .prompt-text {
    font-size: 12px;
    line-height: 1.5;
    margin: 2px 0 4px 0;  /* Slightly more margin at bottom for separation */
    background: rgba(107, 114, 128, 0.03);  /* Very subtle background */
    padding: 3px 4px;
    border-radius: 3px;
  }
  
  .text-label {
    font-weight: 500;
    color: var(--text-tertiary);
    margin-right: 3px;  /* Reduced from 6px */
    display: inline-block;
    min-width: 28px;  /* Reduced from 32px */
  }
  
  .prompt-text .text-label {
    min-width: 50px;
  }
  
  .text-content {
    color: var(--text-secondary);
  }
  
  .text-content.preserve-format {
    white-space: pre-wrap;
    word-wrap: break-word;
    /* Better display for blockquotes and nested structures */
    font-family: var(--font-mono);
    font-size: 0.95em;
    line-height: 1.6;
    display: block;
  }
  
  .suggested-text .text-content {
    color: var(--text-primary);
  }
  
  .prompt-text .text-content {
    color: var(--text-secondary);  /* Same as original text */
  }
  
  .loading-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    font-size: 13px;
    color: var(--text-secondary);
  }
  
  .spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--border-primary);
    border-top-color: var(--accent-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  /* Error state styles */
  .error-state {
    padding: 8px 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .error-message {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--error, #ef4444);
    font-size: 12px;
  }
  
  .error-icon {
    flex-shrink: 0;
  }
  
  .error-text {
    flex: 1;
    line-height: 1.4;
  }
  
  .retry-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-primary);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 11px;
    cursor: pointer;
    transition: all 0.15s ease;
    align-self: flex-start;
  }
  
  .retry-btn:hover:not(:disabled) {
    background: var(--accent-primary);
    color: white;
    border-color: var(--accent-primary);
  }
  
  .retry-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .retry-btn :global(svg) {
    width: 10px;
    height: 10px;
  }
  
  /* Manual edit diff display */
  .manual-edit-diff {
    font-size: 12px;
    line-height: 1.5;
    color: var(--text-secondary);
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--border-primary);
  }
  
  .diff-removed,
  .diff-added {
    margin: 3px 0;
  }
  
  .diff-line {
    display: flex;
    align-items: baseline;
    margin: 2px 0;
    padding: 2px 0;
  }
  
  .diff-marker {
    flex-shrink: 0;
    width: 12px;
    font-family: 'Monaco', 'Menlo', monospace;
    font-weight: bold;
    margin-right: 6px;
  }
  
  .diff-removed .diff-marker {
    color: var(--error);
  }
  
  .diff-added .diff-marker {
    color: var(--success);
  }
  
  .diff-text {
    flex: 1;
    word-break: keep-all;
    line-height: 1.4;
  }
  
  /* Context and change text styling */
  :global(.diff-text .context) {
    color: var(--text-tertiary);
    opacity: 0.7;
    font-size: 11px;
  }
  
  :global(.diff-text .change-text) {
    padding: 0 2px;
    border-radius: 2px;
    font-weight: 500;
  }
  
  :global(.diff-text .change-text.removed) {
    background: rgba(239, 68, 68, 0.15);
    color: var(--error);
    text-decoration: line-through;
  }
  
  :global(.diff-text .change-text.added) {
    background: rgba(34, 197, 94, 0.15);
    color: var(--success);
  }
  
  /* Dark theme adjustments for diff */
  
  
  
  /* AI tool actions (bottom hover toolbar) */
  .ai-tools {
    display: flex;
    gap: 6px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--border-primary);
    opacity: 0;
    animation: fadeIn 0.15s ease forwards;
  }
  
  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }
  
  .tool-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    background: transparent;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }
  
  .tool-btn:hover:not(:disabled) {
    background: var(--bg-tertiary);
    color: var(--accent-primary);
  }
  
  .tool-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .tool-btn :global(svg) {
    width: 16px;
    height: 16px;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  
  /* Dark theme adjustments */
  :global(.dark-theme) .ai-action-card {
    background: var(--bg-secondary);
  }
  
  :global(.dark-theme) .ai-action-card:hover {
    background: var(--bg-tertiary);
  }
  
  :global(.dark-theme) .ai-action-card.selected {
    background: rgba(124, 122, 255, 0.1);
  }
  
  :global(.dark-theme) .segment-number {
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-primary);
  }
  
  :global(.dark-theme) .ai-action-card.ignored {
    background: rgba(255, 255, 255, 0.02);
  }
  
  /* Card-specific Generation Bar Styles - Similar to GenerationBar but adapted for cards */
  .card-generation-bar {
    padding: 2px 0;
    background: transparent;
    margin-top: 1px;
    position: relative;
    z-index: 100;
  }
  
  .card-chat-container {
    position: relative;
    background: var(--bg-primary, white);
    border: 1px solid var(--border-primary, #e5e7eb);
    border-radius: 10px;
    padding: 8px 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: all 0.2s;
  }
  
  .card-chat-container:focus-within {
    border-color: var(--border-secondary, #d1d5db);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
  
  /* Main textarea */
  .card-prompt-input {
    width: 100%;
    padding: 1px 8px;
    padding-bottom: 24px; /* Space for bottom controls */
    font-size: 12px;
    line-height: 1.4;
    border: none;
    background: transparent;
    color: var(--text-primary, #1a1a1a);
    outline: none;
    resize: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    min-height: 20px;
    max-height: 80px;
    overflow-y: auto;
  }
  
  .card-prompt-input::placeholder {
    color: var(--text-tertiary, #9ca3af);
    font-size: 12px;
  }
  
  /* Custom scrollbar */
  .card-prompt-input::-webkit-scrollbar {
    width: 4px;
  }
  
  .card-prompt-input::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .card-prompt-input::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 2px;
  }
  
  /* Bottom controls */
  .card-bottom-controls {
    position: absolute;
    bottom: 5px;
    left: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  /* Model selector */
  .card-model-selector {
    position: relative;
    z-index: 1000;
  }
  
  .card-model-btn {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 3px 6px;
    background: transparent;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.15s;
    color: #6b7280;
  }
  
  .card-model-btn:hover {
    background: #f3f4f6;
    color: #374151;
  }
  
  .card-model-name {
    font-size: 10px;
    font-weight: 500;
  }
  
  .card-chevron-icon {
    transition: transform 0.2s;
    opacity: 0.6;
  }
  
  .card-chevron-icon.rotated {
    transform: rotate(180deg);
  }
  
  /* Model dropdown */
  .card-model-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 2px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    max-height: 200px;
    min-width: 150px;
    overflow: hidden;
    z-index: 9999;
  }
  
  /* Dropdown opens upward when near bottom */
  .card-model-dropdown.dropdown-up {
    top: auto;
    bottom: 100%;
    margin-top: 0;
    margin-bottom: 2px;
  }
  
  .card-model-list {
    max-height: 200px;
    overflow-y: auto;
    padding: 3px;
  }
  
  /* Hide scrollbar but keep functionality */
  .card-model-list::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  
  .card-model-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 6px;
    width: 100%;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    text-align: left;
  }
  
  .card-model-item:hover {
    background: #f3f4f6;
  }
  
  .card-model-item.selected {
    background: #ede9fe;
  }
  
  .card-item-info {
    flex: 1;
  }
  
  .card-item-name {
    font-size: 10px;
    font-weight: 500;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 3px;
  }
  
  .card-recommended-badge {
    font-size: 8px;
    padding: 0px 3px;
    background: #fef3c7;
    color: #92400e;
    border-radius: 3px;
    font-weight: 600;
  }
  
  .card-check-icon {
    flex-shrink: 0;
  }
  
  .card-right-controls {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  /* Character count */
  .card-char-count {
    font-size: 10px;
    color: #9ca3af;
    font-family: 'SF Mono', Monaco, monospace;
    opacity: 0.7;
  }
  
  /* Processing state */
  .card-processing-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px;
    min-height: 28px;
  }
  
  .card-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #e5e7eb;
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: card-spin 0.8s linear infinite;
  }
  
  @keyframes card-spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  .card-processing-text {
    font-size: 12px;
    color: #6b7280;
  }
  
  .card-retry-icon {
    margin-left: auto;
    background: transparent;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: var(--text-tertiary);
    border-radius: 3px;
    transition: all 0.15s ease;
  }
  
  .card-retry-icon:hover {
    background: var(--bg-tertiary);
    color: var(--accent-primary);
  }
  
  /* Send button */
  .card-send-btn {
    width: 24px;
    height: 24px;
    border-radius: 5px;
    background: #5856d6;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }
  
  .card-send-btn:hover:not(:disabled) {
    background: #4744c9;
    transform: scale(1.05);
  }
  
  .card-send-btn:disabled {
    background: #e5e7eb;
    cursor: not-allowed;
  }
  
  .card-send-btn:not(:disabled) :global(svg) {
    width: 12px;
    height: 12px;
    stroke: white !important;
    color: white !important;
  }
  
  .card-send-btn:not(:disabled) :global(svg *) {
    stroke: white !important;
    fill: none !important;
  }
  
  .card-send-btn:disabled :global(svg) {
    width: 12px;
    height: 12px;
    stroke: #9ca3af !important;
    color: #9ca3af !important;
  }
  
  .card-send-btn:disabled :global(svg *) {
    stroke: #9ca3af !important;
    fill: none !important;
  }
  
  /* Dark theme adjustments */
  :global(:root.dark-theme) .card-chat-container:focus-within {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }
  
  :global(:root.dark-theme) .card-prompt-input::-webkit-scrollbar-thumb {
    background: var(--border-secondary);
  }
  
  /* Dark mode for model selector */
  :global(.dark-theme) .card-model-btn {
    color: #9ca3af;
  }
  
  :global(.dark-theme) .card-model-btn:hover {
    background: #374151;
    color: #e5e7eb;
  }
  
  :global(.dark-theme) .card-model-dropdown {
    background: #1f2937;
    border-color: #374151;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  }
  
  :global(.dark-theme) .card-model-item {
    color: #e5e7eb;
  }
  
  :global(.dark-theme) .card-model-item:hover {
    background: #374151;
  }
  
  :global(.dark-theme) .card-model-item.selected {
    background: rgba(99, 102, 241, 0.2);
  }
  
  :global(.dark-theme) .card-item-name {
    color: #e5e7eb;
  }
  
  :global(.dark-theme) .card-recommended-badge {
    background: rgba(251, 191, 36, 0.2);
    color: #fbbf24;
  }
  
  /* Auto theme (system dark mode) support for model dropdown */
  @media (prefers-color-scheme: dark) {
    .card-model-btn {
      background: transparent;
      color: #9ca3af;
    }
    
    .card-model-btn:hover {
      background: #374151;
      color: #e5e7eb;
    }
    
    .card-model-dropdown {
      background: #1f2937;
      border-color: #374151;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
    }
    
    .card-model-item {
      color: #e5e7eb;
    }
    
    .card-model-item:hover {
      background: #374151;
    }
    
    .card-model-item.selected {
      background: rgba(99, 102, 241, 0.2);
    }
    
    .card-item-name {
      color: #e5e7eb;
    }
    
    .card-recommended-badge {
      background: rgba(251, 191, 36, 0.2);
      color: #fbbf24;
    }
    
    .card-provider-icon {
      filter: brightness(0) invert(1);
    }
    
    .card-chevron-icon {
      filter: invert(1);
      opacity: 0.7;
    }
  }
  
  :global(:root.dark-theme) .card-send-btn {
    background: var(--accent-primary);
  }
  
  :global(:root.dark-theme) .card-send-btn:hover:not(:disabled) {
    background: var(--accent-secondary);
  }
  
  :global(:root.dark-theme) .card-send-btn:disabled {
    background: var(--bg-tertiary);
    color: var(--text-tertiary);
  }
  
  :global(:root.dark-theme) .card-send-btn:not(:disabled) :global(svg) {
    stroke: white !important;
    color: white !important;
  }
  
  :global(:root.dark-theme) .card-send-btn:not(:disabled) :global(svg *) {
    stroke: white !important;
    fill: none !important;
  }
  
  :global(:root.dark-theme) .card-send-btn:disabled :global(svg) {
    stroke: var(--text-tertiary) !important;
    color: var(--text-tertiary) !important;
  }
  
  :global(:root.dark-theme) .card-send-btn:disabled :global(svg *) {
    stroke: var(--text-tertiary) !important;
    fill: none !important;
  }
  
  :global(:root.dark-theme) .card-processing-text {
    color: var(--text-secondary);
  }
  
  :global(:root.dark-theme) .card-spinner {
    border-color: var(--border-primary);
    border-top-color: var(--accent-primary);
  }
  
  /* Apply white filter to provider icons in dark mode */
  :global(:root.dark-theme) .card-provider-icon {
    filter: brightness(0) invert(1);
  }
  
  :global(:root.dark-theme) .card-chevron-icon {
    filter: invert(1);
    opacity: 0.7;
  }
  
  /* Dark mode styles for reason section */
  :global(.dark-theme) .reason-toggle:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--text-primary);
  }
  
  :global(.dark-theme) .reason-content {
    background: rgba(255, 255, 255, 0.03);
  }
  
  :global(.dark-theme) .toggle-icon {
    color: var(--text-tertiary);
  }
  
  :global(.dark-theme) .reason-section {
    border-top-color: rgba(255, 255, 255, 0.1);
  }
  
  /* Dark mode styles for split view content */
  :global(.dark-theme) .reason-display {
    background: transparent;
    border-left: 1px solid var(--accent-secondary);
    color: var(--text-secondary);
    opacity: 0.65;
  }
  
  :global(.dark-theme) .prompt-display .prompt-label {
    color: var(--text-secondary);
  }
  
  :global(.dark-theme) .prompt-display .prompt-value {
    color: var(--text-primary);
  }
  
  :global(.dark-theme) .splitview-content .loading-text,
  :global(.dark-theme) .splitview-content .no-change-text,
  :global(.dark-theme) .splitview-content .processing-text {
    color: var(--text-secondary);
    opacity: 0.8;
  }
  
  /* Global SVG styling for lucide-svelte icons */
  .action-icon :global(svg),
  .tool-btn :global(svg),
  .error-icon :global(svg) {
    stroke-width: 1.5;
  }
  
  .card-chevron-icon :global(svg) {
    stroke-width: 2.5;
  }
  
  .card-check-icon :global(svg) {
    stroke: #6366f1;
    stroke-width: 2;
  }
  
  /* Ensure stroke width is consistent for all states */
  .card-send-btn :global(svg) {
    stroke-width: 2;
  }
  
  /* Additional specificity to ensure icon colors work */
  .card-generation-bar .card-send-btn:not(:disabled) :global(svg),
  .card-generation-bar .card-send-btn:not(:disabled) :global(svg path) {
    stroke: white !important;
    fill: none !important;
  }
  
  .card-generation-bar .card-send-btn:disabled :global(svg),
  .card-generation-bar .card-send-btn:disabled :global(svg path) {
    stroke: #9ca3af !important; 
    fill: none !important;
  }
</style>